import { useEffect, useRef, useState } from "react";
import Head from "next/head";
import Link from "next/link";
import { useRouter } from "next/router";
import type { GetServerSideProps } from "next";
import { getAccountPageProps } from "@/lib/account-page";
import { useFirebaseAuth } from "@/lib/firebase-auth-context";
import {
  requestReorder,
  type ReorderLine,
} from "@/lib/customer-orders";

export const getServerSideProps: GetServerSideProps = async (context) => {
  return { props: await getAccountPageProps(context, "Order again") };
};

/**
 * Web-only "Order again" path. Splits off the Flutter `/app/checkout` bridge
 * so signed-in web customers DON'T get dragged through the mobile-shell
 * completion logic (deep links, returnTo plumbing, etc.) for a reorder.
 *
 * Flow:
 *   1. Mint a reorder result via `requestReorder(token)` — the CMS revalidates
 *      the past order's items against today's menu and returns the cart
 *      `lines` we can re-add.
 *   2. Wait for Snipcart's cart-items API to be ready, then replace the
 *      cart contents with those lines.
 *   3. Open the Snipcart cart drawer; the customer reviews + clicks Checkout
 *      themselves (one extra click vs jumping straight to billing, matches
 *      normal web shopping). The standard `SnipcartCustomerLink` cart.update
 *      runs in the background and stamps `metadata.firebaseUid` on the cart,
 *      so the resulting order links to the customer's account history.
 */
type ReorderStatus =
  | { kind: "loading"; message: string }
  | { kind: "cannot-reorder"; reason: string }
  | { kind: "ready" }
  | { kind: "error"; message: string };

// Reuse the same Snipcart product shape /app/checkout's bridge sends, so
// Snipcart's pipe-delimited options parsing + custom-fields handling stay
// consistent across the two reorder paths.
type SnipcartProductInput = {
  id: string;
  name: string;
  price: number | string;
  url: string;
  description?: string;
  image?: string;
  quantity: number;
  stackable?: string;
  customFields?: Array<{
    name: string;
    type?: string;
    required?: boolean;
    value?: string;
    options?: string;
  }>;
};

type SnipcartItemsApi = {
  replace?: (...products: SnipcartProductInput[]) => Promise<unknown>;
  add?: (...products: SnipcartProductInput[]) => Promise<unknown>;
  remove?: (uniqueId: string) => Promise<unknown>;
};

type SnipcartCartUpdatePayload = {
  metadata?: Record<string, unknown>;
};

type SnipcartWindow = {
  __rlFirebaseUid?: string;
  __rlFirebaseEmail?: string;
  Snipcart?: {
    api?: {
      cart?: {
        items?: SnipcartItemsApi;
        update?: (data: SnipcartCartUpdatePayload) => Promise<unknown>;
      };
      theme?: { cart?: { open?: () => void } };
    };
    store?: {
      getState?: () => {
        cart?: {
          items?: { items?: Array<{ uniqueId?: string }> };
          metadata?: Record<string, unknown> | null;
        };
      };
    };
  };
};

function readCurrentCartMetadata(win: SnipcartWindow): Record<string, unknown> {
  try {
    const metadata = win.Snipcart?.store?.getState?.()?.cart?.metadata;
    if (metadata && typeof metadata === "object" && !Array.isArray(metadata)) {
      return { ...(metadata as Record<string, unknown>) };
    }
  } catch {
    /* store not ready or shape changed — fall through */
  }
  return {};
}

function buildProductDefinition(line: ReorderLine): SnipcartProductInput {
  return {
    id: line.product.itemId,
    name: line.product.name,
    price: line.product.price,
    url: line.product.url,
    description: line.product.description,
    image: line.product.imageUrl || undefined,
    quantity: line.quantity,
    stackable:
      line.product.buttonAttributes?.["data-item-stackable"] || undefined,
    customFields: (line.product.customFields ?? []).map((field) => ({
      name: field.name,
      type: field.type,
      required: field.required,
      value: field.value || undefined,
      // Snipcart's cart.items.add JS API parses options as a pipe-delimited
      // "Small|Medium[+5.00]|Large[+10.00]" string. The CMS publishes that
      // on `optionsText` for every reorder line; arrays don't work here.
      options: field.optionsText || undefined,
    })),
  };
}

async function waitForSnipcartItemsApi(
  timeoutMs = 30_000,
): Promise<SnipcartItemsApi | null> {
  if (typeof window === "undefined") return null;
  const win = window as unknown as SnipcartWindow;
  const start = Date.now();
  while (Date.now() - start < timeoutMs) {
    const itemsApi = win.Snipcart?.api?.cart?.items;
    if (itemsApi && (itemsApi.replace || itemsApi.add)) {
      return itemsApi;
    }
    await new Promise((resolve) => setTimeout(resolve, 200));
  }
  return null;
}

async function replaceCartContents(
  itemsApi: SnipcartItemsApi,
  products: SnipcartProductInput[],
): Promise<void> {
  if (typeof itemsApi.replace === "function") {
    await itemsApi.replace(...products);
    return;
  }
  // Fallback for older Snipcart versions: remove existing items one-by-one,
  // then add the new ones. Mirrors /app/checkout.tsx's replaceCartContents.
  const win = window as unknown as SnipcartWindow;
  const currentItems =
    win.Snipcart?.store?.getState?.()?.cart?.items?.items ?? [];
  for (const item of currentItems) {
    if (!item?.uniqueId) continue;
    await itemsApi.remove?.(item.uniqueId);
  }
  if (products.length > 0 && itemsApi.add) {
    await itemsApi.add(...products);
  }
}

function describeReorderReason(reason: string | null): string {
  switch (reason) {
    case "product_not_found":
      return "One or more items from this order are no longer on the menu.";
    case "variant_option_invalid":
    case "variant_required":
      return "The options on this order are no longer available.";
    case "site_mismatch":
      return "This order was placed on a different Aso Rock store.";
    case "manifest_empty":
      return "This order has no reorderable items.";
    default:
      return "This order can't be reordered right now.";
  }
}

export default function WebReorderPage() {
  const router = useRouter();
  const { user, loading, getIdToken } = useFirebaseAuth();
  const token =
    typeof router.query.token === "string" ? router.query.token : "";
  const [status, setStatus] = useState<ReorderStatus>({
    kind: "loading",
    message: "Preparing your previous order...",
  });
  // Don't re-run the reorder effect if React strict-mode double-fires it or if
  // the user clicks something that re-renders the page.
  const ranRef = useRef(false);

  useEffect(() => {
    if (!loading && !user) {
      void router.replace("/sign-in");
    }
  }, [loading, user, router]);

  useEffect(() => {
    if (!user || !token || ranRef.current) return;
    ranRef.current = true;
    let cancelled = false;

    async function run() {
      try {
        setStatus({
          kind: "loading",
          message: "Looking up your previous order...",
        });
        const idToken = await getIdToken();
        if (!idToken) throw new Error("no-token");
        const result = await requestReorder(token, idToken);
        if (cancelled) return;

        if (!result.canReorder) {
          setStatus({
            kind: "cannot-reorder",
            reason: describeReorderReason(result.reason),
          });
          return;
        }
        const lines = result.lines ?? [];
        if (lines.length === 0) {
          setStatus({
            kind: "cannot-reorder",
            reason: describeReorderReason("manifest_empty"),
          });
          return;
        }

        setStatus({
          kind: "loading",
          message: "Adding the items to your basket...",
        });
        const itemsApi = await waitForSnipcartItemsApi();
        if (cancelled) return;
        if (!itemsApi) {
          setStatus({
            kind: "error",
            message:
              "Your basket isn't ready yet. Please refresh and try again.",
          });
          return;
        }

        // Set the legacy window globals BEFORE the replace fires the
        // `item.added` chain in snipcart.tsx. Its writeReorderManifest reads
        // `window.__rlFirebaseUid` synchronously and will *delete* the
        // firebaseUid key from cart metadata if the global is missing —
        // see snipcart.tsx buildCartMetadataUpdate(). The auth-link
        // component sets these too, but its useEffect timing can lag the
        // synchronous events Snipcart fires.
        const win = window as unknown as SnipcartWindow;
        if (user?.uid) {
          win.__rlFirebaseUid = user.uid;
          if (user.email) {
            win.__rlFirebaseEmail = user.email;
          }
        }

        const products = lines.map(buildProductDefinition);
        await replaceCartContents(itemsApi, products);
        if (cancelled) return;

        // Open Snipcart's regular cart drawer; the customer reviews and then
        // clicks Checkout.
        try {
          win.Snipcart?.api?.theme?.cart?.open?.();
        } catch {
          /* harmless — drawer will open on next user interaction */
        }

        // Belt-and-braces: explicitly re-stamp `metadata.firebaseUid` on the
        // cart AFTER any debounced writeReorderManifest from the replace
        // chain has settled. snipcart.tsx's writeReorderManifest runs on a
        // 200ms debounce after `item.added`, so we wait a generous beat
        // before our final write to make sure ours is last. Spreads the
        // current metadata under our addition so we never wipe siteCode,
        // reorderManifestV1, etc.
        await new Promise((resolve) => setTimeout(resolve, 600));
        if (cancelled) return;
        const cartUpdate = win.Snipcart?.api?.cart?.update;
        if (cartUpdate && user?.uid) {
          try {
            const baseMetadata = readCurrentCartMetadata(win);
            const finalMetadata: Record<string, unknown> = {
              ...baseMetadata,
              firebaseUid: user.uid,
            };
            if (user.email) finalMetadata.firebaseEmail = user.email;
            await cartUpdate({ metadata: finalMetadata });
            // eslint-disable-next-line no-console
            console.log("[reorder] firebaseUid stamped on cart metadata", {
              uid: user.uid.slice(0, 8) + "...",
              metadataKeys: Object.keys(finalMetadata),
            });
          } catch (error) {
            // eslint-disable-next-line no-console
            console.error("[reorder] failed to stamp firebaseUid", error);
          }
        }
        setStatus({ kind: "ready" });
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[reorder] failed", error);
        if (!cancelled) {
          setStatus({
            kind: "error",
            message:
              "We couldn't reorder this right now. Please try again or browse the menu.",
          });
        }
      }
    }

    void run();
    return () => {
      cancelled = true;
    };
  }, [user, token, getIdToken]);

  if (loading || !user) {
    return (
      <div className="storefront-page-backdrop min-h-[60vh] px-4 py-16 text-center">
        <p className="storefront-page-body">Loading...</p>
      </div>
    );
  }

  return (
    <>
      <Head>
        <title>Order again</title>
        <meta name="robots" content="noindex,nofollow" />
      </Head>
      <div className="storefront-page-backdrop min-h-[60vh] px-4 py-12 sm:py-16">
        <div className="mx-auto w-full max-w-xl text-center">
          <Link
            href={token ? `/account/orders/${encodeURIComponent(token)}` : "/account/orders"}
            className="storefront-page-link text-sm font-semibold"
          >
            ← Back to the order
          </Link>
          <h1 className="storefront-page-title mt-3 text-[1.75rem] font-bold tracking-tight">
            Order again
          </h1>

          {status.kind === "loading" ? (
            <p className="storefront-page-body mt-6 text-sm leading-6">
              {status.message}
            </p>
          ) : null}

          {status.kind === "ready" ? (
            <div className="storefront-page-panel mt-6 rounded-[2rem] px-6 py-6 sm:px-8">
              <p className="storefront-page-body text-sm leading-6">
                Your previous order is in your basket. Review it on the right
                and tap <strong>Checkout</strong> when you&apos;re ready.
              </p>
              <Link
                href="/menu"
                className="storefront-page-link mt-4 inline-block text-sm font-semibold"
              >
                Or keep browsing the menu →
              </Link>
            </div>
          ) : null}

          {status.kind === "cannot-reorder" ? (
            <div className="storefront-page-panel mt-6 rounded-[2rem] px-6 py-6 sm:px-8">
              <p className="storefront-page-body text-sm leading-6">
                {status.reason}
              </p>
              <Link
                href="/menu"
                className="storefront-page-button mt-5 inline-flex min-h-12 items-center justify-center rounded-full px-6 text-base font-semibold"
              >
                Browse the menu
              </Link>
            </div>
          ) : null}

          {status.kind === "error" ? (
            <div className="storefront-page-panel mt-6 rounded-[2rem] px-6 py-6 sm:px-8">
              <p className="storefront-page-body text-sm leading-6 text-red-700">
                {status.message}
              </p>
              <button
                type="button"
                onClick={() => {
                  ranRef.current = false;
                  setStatus({
                    kind: "loading",
                    message: "Trying again...",
                  });
                  router.replace(router.asPath);
                }}
                className="storefront-page-button mt-5 inline-flex min-h-12 items-center justify-center rounded-full px-6 text-base font-semibold"
              >
                Try again
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
